import { formatDateDDMMYY } from '../../utils/dateFormat';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BlockIcon from '@mui/icons-material/Block';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import LoginIcon from '@mui/icons-material/Login';
import PeopleIcon from '@mui/icons-material/People';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningIcon from '@mui/icons-material/Warning';
import ArchiveIcon from '@mui/icons-material/Archive';
import EditIcon from '@mui/icons-material/Edit';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';

import {
  getAdminUsers,
  createAdminUser,
  archiveUserAdmin,
  approveUser,
  rejectUser,
  impersonateUser,
  disableUserAdmin,
  enableUserAdmin,
  restoreUserAdmin,
  AdminUserView,
  CreateAdminUserPayload,
} from '../../services/adminService';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { setTokens, getAccessToken, getRefreshToken } from '../../utils/tokenUtils';

type QueueFilter = 'all' | 'AWAITING_REVIEW' | 'ACTIVE' | 'TRIAL' | 'DISABLED' | 'SUSPENDED' | 'CANCELLED' | 'HIGH_RISK';
type ActionType = 'APPROVE' | 'REJECT' | 'DISABLE' | 'ENABLE' | 'ARCHIVE' | 'RESTORE' | null;

type CreateUserForm = CreateAdminUserPayload & { confirmPassword: string };

const newCreateUserForm = (): CreateUserForm => ({
  email: '',
  password: '',
  confirmPassword: '',
  firstName: '',
  lastName: '',
  phone: '',
  country: '',
  role: 'BROADCASTER',
  streamName: '',
});

const cardSx = { borderRadius: 0, border: '0.5px solid', borderColor: 'divider', bgcolor: 'background.paper' } as const;
const labelSx = { fontSize: '0.64rem', fontWeight: 800, textTransform: 'uppercase' as const, letterSpacing: '0.08em', color: 'text.disabled' };
const monoSx = { fontFamily: "'DM Mono', monospace", fontSize: '0.76rem', fontWeight: 800 };
const iconSx = { borderRadius: 1, border: '0.5px solid', borderColor: 'divider', width: 30, height: 30 } as const;

const riskValue = (user: AdminUserView) => Number((user as any).billingRiskScore || 0);
const riskLevel = (user: AdminUserView) => user.riskProfile?.level || (riskValue(user) >= 75 ? 'CRITICAL' : riskValue(user) >= 50 ? 'HIGH' : riskValue(user) >= 25 ? 'MODERATE' : 'LOW');
const riskColor = (user: AdminUserView): 'success' | 'warning' | 'error' => riskLevel(user) === 'LOW' ? 'success' : riskLevel(user) === 'MODERATE' ? 'warning' : 'error';
const riskSummary = (user: AdminUserView) => user.riskProfile?.factors?.slice(0, 2).map((factor) => factor.detail).join(' ') || 'No current billing risk evidence.';
const userRef = (user: AdminUserView) => String((user as any).hid || user.id);
const isPendingApproval = (status: string) => ['AWAITING_REVIEW', 'PENDING_APPROVAL'].includes(status);

const statusConfig = (status: string) => {
  const map: any = {
    ACTIVE: { color: 'success', icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
    AWAITING_REVIEW: { color: 'warning', icon: <HourglassEmptyIcon sx={{ fontSize: 14 }} /> },
    PENDING_APPROVAL: { color: 'warning', icon: <HourglassEmptyIcon sx={{ fontSize: 14 }} /> },
    REJECTED: { color: 'error', icon: <CancelIcon sx={{ fontSize: 14 }} /> },
    DISABLED: { color: 'error', icon: <BlockIcon sx={{ fontSize: 14 }} /> },
    SUSPENDED: { color: 'error', icon: <BlockIcon sx={{ fontSize: 14 }} /> },
    CANCELLED: { color: 'default', icon: <ArchiveIcon sx={{ fontSize: 14 }} /> },
    GRACE_PERIOD: { color: 'warning', icon: <WarningIcon sx={{ fontSize: 14 }} /> },
    PAST_DUE: { color: 'warning', icon: <WarningIcon sx={{ fontSize: 14 }} /> },
    TRIAL: { color: 'info', icon: <TrendingUpIcon sx={{ fontSize: 14 }} /> },
    TRIAL_EXPIRED: { color: 'error', icon: <WarningIcon sx={{ fontSize: 14 }} /> },
  };
  return map[status] || { color: 'default', icon: null };
};

const StatusChip = ({ status }: { status: string }) => {
  const config = statusConfig(status);
  return <Chip label={status.replace('_', ' ')} color={config.color} icon={config.icon} size="small" sx={{ borderRadius: 0, fontWeight: 800, fontSize: '0.68rem' }} />;
};

const QueueMetric = ({ label, value, color, onClick }: { label: string; value: React.ReactNode; color: string; onClick?: () => void }) => (
  <Box onClick={onClick} sx={{ minWidth: 118, borderLeft: `3px solid ${color}`, pl: 1.25, cursor: onClick ? 'pointer' : 'default' }}>
    <Typography sx={labelSx}>{label}</Typography>
    <Typography sx={{ fontSize: '1.15rem', fontWeight: 900, lineHeight: 1.1 }}>{value}</Typography>
  </Box>
);

export const UserManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();

  const [users, setUsers] = useState<AdminUserView[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState<QueueFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<AdminUserView | null>(null);
  const [actionType, setActionType] = useState<ActionType>(null);
  const [reason, setReason] = useState('');
  const [typedConfirm, setTypedConfirm] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [impersonateOpen, setImpersonateOpen] = useState(false);
  const [impersonateTarget, setImpersonateTarget] = useState<AdminUserView | null>(null);
  const [impersonateReason, setImpersonateReason] = useState('');
  const [impersonateConfirm, setImpersonateConfirm] = useState('');
  const [impersonateLoading, setImpersonateLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createForm, setCreateForm] = useState<CreateUserForm>(newCreateUserForm);

  const backendStatus = filterStatus;

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await getAdminUsers({ page, limit: 10, status: backendStatus, search: searchTerm });
      setUsers(data.users.map((user) => user.accountStatus === 'PENDING_APPROVAL'
        ? { ...user, accountStatus: 'AWAITING_REVIEW' }
        : user));
      setTotalUsers(data.total);
      setTotalPages(data.totalPages);
    } catch (err) {
      console.error(err);
      showSnackbar('Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(fetchUsers, 300);
    return () => clearTimeout(timeoutId);
  }, [page, backendStatus, searchTerm]);

  const visibleUsers = users;

  const stats = useMemo(() => ({
    total: totalUsers,
    pending: users.filter((u) => isPendingApproval(u.accountStatus)).length,
    active: users.filter((u) => u.accountStatus === 'ACTIVE').length,
    disabled: users.filter((u) => u.accountStatus === 'DISABLED').length,
    highRisk: users.filter((u) => ['HIGH', 'CRITICAL'].includes(riskLevel(u))).length,
  }), [totalUsers, users]);

  const setQueueFilter = (value: QueueFilter) => { setFilterStatus(value); setPage(1); };

  const updateCreateField = <K extends keyof CreateUserForm>(field: K, value: CreateUserForm[K]) => {
    setCreateForm((current) => ({ ...current, [field]: value }));
  };

  const closeCreateDialog = () => {
    if (createLoading) return;
    setCreateOpen(false);
    setCreateForm(newCreateUserForm());
  };

  const handleCreateUser = async () => {
    if (createForm.password !== createForm.confirmPassword) {
      showSnackbar('Temporary passwords do not match', 'error');
      return;
    }
    if (createForm.password.length < 12) {
      showSnackbar('Temporary password must be at least 12 characters', 'error');
      return;
    }

    setCreateLoading(true);
    try {
      const payload: CreateAdminUserPayload = {
        email: createForm.email,
        password: createForm.password,
        firstName: createForm.firstName,
        lastName: createForm.lastName,
        phone: createForm.phone,
        country: createForm.country,
        role: createForm.role,
        streamName: createForm.streamName,
      };
      const result = await createAdminUser(payload);
      showSnackbar(`${result.user.email} created and added to Pending Review`, 'success');
      setCreateOpen(false);
      setCreateForm(newCreateUserForm());
      if (filterStatus === 'AWAITING_REVIEW' && page === 1) await fetchUsers();
      else setQueueFilter('AWAITING_REVIEW');
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Failed to create user', 'error');
    } finally {
      setCreateLoading(false);
    }
  };

  const openActionDialog = (user: AdminUserView, type: ActionType) => {
    setSelectedUser(user);
    setActionType(type);
    setReason('');
    setTypedConfirm('');
  };

  const closeActionDialog = () => {
    setActionType(null);
    setSelectedUser(null);
    setReason('');
    setTypedConfirm('');
  };

  const handleActionSubmit = async () => {
    if (!selectedUser || !actionType) return;
    const ref = userRef(selectedUser) as any;
    setActionLoading(true);
    try {
      if (actionType === 'APPROVE') {
        await approveUser(ref, reason);
        showSnackbar('User approved successfully', 'success');
      } else if (actionType === 'REJECT') {
        await rejectUser(ref, reason);
        showSnackbar('User rejected and disabled', 'info');
      } else if (actionType === 'DISABLE') {
        await disableUserAdmin(ref, reason);
        showSnackbar('User disabled. Credentials are now blocked.', 'success');
      } else if (actionType === 'ENABLE') {
        await enableUserAdmin(ref, reason, 'ACTIVE');
        showSnackbar('User enabled successfully', 'success');
      } else if (actionType === 'ARCHIVE') {
        await archiveUserAdmin(ref, reason, typedConfirm);
        showSnackbar('User archived. Services are stopped and records are retained.', 'success');
      } else if (actionType === 'RESTORE') {
        await restoreUserAdmin(ref, reason);
        showSnackbar('User restored successfully', 'success');
      }
      fetchUsers();
      closeActionDialog();
    } catch (e: any) {
      showSnackbar(e.response?.data?.message || 'Action failed', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const openImpersonateDialog = (user: AdminUserView) => {
    setImpersonateTarget(user);
    setImpersonateReason('');
    setImpersonateConfirm('');
    setImpersonateOpen(true);
  };

  const handleImpersonate = async () => {
    if (!impersonateTarget || impersonateConfirm !== 'IMPERSONATE' || !impersonateReason.trim()) return;
    setImpersonateLoading(true);
    try {
      const adminAccessToken = getAccessToken();
      const adminRefreshToken = getRefreshToken();
      if (adminAccessToken && adminRefreshToken) {
        sessionStorage.setItem('admin_original_accessToken', adminAccessToken);
        sessionStorage.setItem('admin_original_refreshToken', adminRefreshToken);
      }
      const res = await impersonateUser(userRef(impersonateTarget) as any, impersonateReason.trim());
      sessionStorage.setItem('admin_impersonation_reason', impersonateReason.trim());
      sessionStorage.setItem('admin_impersonation_user', impersonateTarget.email);
      sessionStorage.setItem('admin_impersonation_started_at', new Date().toISOString());
      setTokens(res.accessToken, res.refreshToken);
      window.location.href = '/console';
    } catch (e: any) {
      showSnackbar(e.response?.data?.message || 'Impersonation failed', 'error');
      setImpersonateLoading(false);
    }
  };

  const renderApprovalCard = (user: AdminUserView) => (
    <Paper key={user.id} elevation={0} sx={{ ...cardSx, p: 1.25, borderLeft: `3px solid ${theme.palette.warning.main}` }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography sx={{ fontSize: '0.88rem', fontWeight: 900 }}>{user.firstName || 'No Name'} {user.lastName}</Typography>
            <Chip label={user.role.toUpperCase()} size="small" sx={{ borderRadius: 0, height: 20, fontSize: '0.62rem', fontWeight: 900 }} />
            <Tooltip title={riskSummary(user)}><Chip label={`${riskLevel(user)} ${riskValue(user)}/100`} size="small" color={riskColor(user)} variant="outlined" sx={{ borderRadius: 1, height: 20, fontSize: '0.62rem', fontWeight: 900 }} /></Tooltip>
          </Stack>
          <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{user.email}</Typography>
          <Typography sx={{ ...monoSx, color: 'text.disabled' }}>{user._count?.channels || 0} channels · joined {formatDateDDMMYY(user.createdAt)}</Typography>
        </Box>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
          <Button size="small" variant="contained" color="success" startIcon={<CheckCircleIcon />} onClick={() => openActionDialog(user, 'APPROVE')} sx={{ borderRadius: 0, textTransform: 'none', fontWeight: 800 }}>Approve</Button>
          <Button size="small" variant="outlined" color="error" startIcon={<CancelIcon />} onClick={() => openActionDialog(user, 'REJECT')} sx={{ borderRadius: 0, textTransform: 'none', fontWeight: 800 }}>Reject</Button>
          <Button size="small" variant="outlined" startIcon={<VisibilityIcon />} onClick={() => navigate(`/admin/users/${userRef(user)}`)} sx={{ borderRadius: 0, textTransform: 'none', fontWeight: 800 }}>Open</Button>
        </Stack>
      </Stack>
    </Paper>
  );

  const actionTitle = ({
    APPROVE: 'Approve Account',
    REJECT: 'Reject Registration',
    DISABLE: 'Disable User',
    ENABLE: 'Enable User',
    ARCHIVE: 'Archive User',
    RESTORE: 'Restore User',
  } as Record<string, string>)[actionType || ''] || 'User Action';
  const requiresTyped = ['DISABLE', 'ENABLE', 'ARCHIVE', 'RESTORE'].includes(actionType || '');
  const requiredWord = actionType || '';
  const reasonRequired = ['REJECT', 'DISABLE', 'ENABLE', 'ARCHIVE', 'RESTORE'].includes(actionType || '');
  const actionDisabled = actionLoading || !actionType || (reasonRequired && !reason.trim()) || (requiresTyped && typedConfirm !== requiredWord);
  const actionDescription = actionType === 'DISABLE'
    ? `Temporarily disable ${selectedUser?.email}. Login and authenticated service access will be blocked until enabled.`
    : actionType === 'ENABLE'
      ? `Enable ${selectedUser?.email} and restore account access.`
      : actionType === 'ARCHIVE'
        ? `Archive ${selectedUser?.email}. Inputs and outputs stop immediately, but billing, channel, and audit records are retained. This can be restored.`
        : actionType === 'RESTORE'
          ? `Restore ${selectedUser?.email} to the appropriate prior billing or trial state.`
          : actionType === 'APPROVE'
            ? `Approve ${selectedUser?.email} as a TRIAL user with ${selectedUser?.role} permissions.`
            : `Reject ${selectedUser?.email}. Rejection disables the account.`;
  const actionButtonLabel = ({
    APPROVE: 'Approve User',
    REJECT: 'Reject User',
    DISABLE: 'Disable User',
    ENABLE: 'Enable User',
    ARCHIVE: 'Archive User',
    RESTORE: 'Restore User',
  } as Record<string, string>)[actionType || ''] || 'Confirm';

  return (
    <Box sx={{ maxWidth: 1700, mx: 'auto' }}>
      <Paper elevation={0} sx={{ ...cardSx, p: 1.5, mb: 1.5 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }}>
          <Stack direction="row" spacing={2.3} alignItems="center" flexWrap="wrap" useFlexGap>
            <Stack direction="row" spacing={1} alignItems="center"><AdminPanelSettingsIcon sx={{ color: 'text.secondary' }} /><Box><Typography sx={{ fontSize: '1rem', fontWeight: 900 }}>User Governance</Typography><Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Create, review, edit, archive, restore, and securely inspect customer accounts</Typography></Box></Stack>
            <QueueMetric label="Total" value={stats.total} color={theme.palette.primary.main} onClick={() => setQueueFilter('all')} />
            <QueueMetric label="Pending" value={stats.pending} color={stats.pending ? theme.palette.warning.main : theme.palette.success.main} onClick={() => setQueueFilter('AWAITING_REVIEW')} />
            <QueueMetric label="Active" value={stats.active} color={theme.palette.success.main} onClick={() => setQueueFilter('ACTIVE')} />
            <QueueMetric label="Disabled" value={stats.disabled} color={theme.palette.error.main} onClick={() => setQueueFilter('DISABLED')} />
            <QueueMetric label="High Risk" value={stats.highRisk} color={stats.highRisk ? theme.palette.error.main : theme.palette.info.main} onClick={() => setQueueFilter('HIGH_RISK')} />
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center"><Button size="small" variant="contained" startIcon={<PersonAddIcon />} onClick={() => setCreateOpen(true)} sx={{ minHeight: 34, borderRadius: 1, textTransform: 'none', fontWeight: 900 }}>Create User</Button><Tooltip title="Refresh users"><span><IconButton size="small" onClick={fetchUsers} disabled={loading} sx={{ ...iconSx, width: 34, height: 34 }}>{loading ? <CircularProgress size={16} /> : <RefreshIcon sx={{ fontSize: 18 }} />}</IconButton></span></Tooltip></Stack>
        </Stack>
      </Paper>

      {stats.pending > 0 && <Paper elevation={0} sx={{ ...cardSx, p: 1.25, mb: 1.5, borderLeft: `3px solid ${theme.palette.warning.main}` }}><Stack spacing={1.1}><Stack direction="row" spacing={1} alignItems="center"><HourglassEmptyIcon sx={{ color: 'warning.main' }} /><Box><Typography sx={{ fontSize: '0.9rem', fontWeight: 900 }}>Pending Review Queue</Typography><Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Approve, reject, or inspect new accounts without hunting through the table.</Typography></Box></Stack><Stack spacing={1}>{users.filter((user) => isPendingApproval(user.accountStatus)).slice(0, 5).map(renderApprovalCard)}</Stack></Stack></Paper>}

      <Paper elevation={0} sx={{ ...cardSx, p: 1.25, mb: 1.5 }}><Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', md: 'center' }}><TextField fullWidth placeholder="Search by email, name, or hash ID..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} variant="outlined" size="small" InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>, sx: { borderRadius: 0 } }} /><TextField select label="Queue" value={filterStatus} onChange={(e) => setQueueFilter(e.target.value as QueueFilter)} size="small" sx={{ minWidth: 190 }} InputProps={{ sx: { borderRadius: 0 } }}><MenuItem value="all">All Accounts</MenuItem><MenuItem value="AWAITING_REVIEW">Pending Review</MenuItem><MenuItem value="ACTIVE">Active</MenuItem><MenuItem value="TRIAL">Trial</MenuItem><MenuItem value="DISABLED">Disabled</MenuItem><MenuItem value="SUSPENDED">Suspended / Banned</MenuItem><MenuItem value="CANCELLED">Archived / Terminated</MenuItem><MenuItem value="HIGH_RISK">High Risk</MenuItem></TextField></Stack></Paper>

      <TableContainer component={Paper} elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}><Table size="small"><TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}><TableRow><TableCell sx={labelSx}>User</TableCell><TableCell sx={labelSx}>Role / Risk</TableCell><TableCell sx={labelSx}>Resources</TableCell><TableCell sx={labelSx}>Status</TableCell><TableCell align="right" sx={labelSx}>Actions</TableCell></TableRow></TableHead><TableBody>{loading ? <TableRow><TableCell colSpan={5} align="center" sx={{ py: 6 }}><CircularProgress size={28} /></TableCell></TableRow> : visibleUsers.length === 0 ? <TableRow><TableCell colSpan={5} align="center" sx={{ py: 6 }}><PeopleIcon sx={{ fontSize: 42, color: 'text.disabled', mb: 1 }} /><Typography color="text.secondary">No users found</Typography></TableCell></TableRow> : visibleUsers.map((user) => <TableRow key={user.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/users/${userRef(user)}`)}><TableCell><Typography sx={{ fontSize: '0.82rem', fontWeight: 900, color: 'primary.main' }}>{user.firstName || 'No Name'} {user.lastName}</Typography><Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{user.email}</Typography><Typography sx={{ ...monoSx, color: 'text.disabled' }}>{formatDateDDMMYY(user.createdAt)}</Typography></TableCell><TableCell><Stack spacing={0.7} alignItems="flex-start"><Chip label={user.role.toUpperCase()} size="small" sx={{ borderRadius: 0, height: 22, fontSize: '0.66rem', fontWeight: 900 }} /><Tooltip title={riskSummary(user)}><Chip label={`${riskLevel(user)} ${riskValue(user)}/100`} size="small" color={riskColor(user)} variant="outlined" sx={{ borderRadius: 1, height: 20, fontSize: '0.62rem', fontWeight: 800 }} /></Tooltip></Stack></TableCell><TableCell><Typography sx={{ fontSize: '0.78rem', fontWeight: 800 }}>{user._count?.channels || 0} Channel{user._count?.channels !== 1 ? 's' : ''}</Typography><Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Account resources</Typography></TableCell><TableCell><StatusChip status={user.accountStatus} /></TableCell><TableCell align="right"><Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap" useFlexGap><Tooltip title="View details"><IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/admin/users/${userRef(user)}`); }} sx={iconSx}><VisibilityIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip><Tooltip title="Edit user"><IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); navigate(`/admin/users/${userRef(user)}?edit=1`); }} sx={iconSx}><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip><Tooltip title="Controlled impersonation"><span><IconButton size="small" disabled={['DISABLED', 'SUSPENDED', 'CANCELLED', 'REJECTED'].includes(user.accountStatus)} onClick={(e) => { e.stopPropagation(); openImpersonateDialog(user); }} sx={iconSx}><LoginIcon sx={{ fontSize: 16 }} /></IconButton></span></Tooltip>{user.accountStatus === 'AWAITING_REVIEW' && <><Tooltip title="Approve"><IconButton size="small" color="success" onClick={(e) => { e.stopPropagation(); openActionDialog(user, 'APPROVE'); }} sx={iconSx}><CheckCircleIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip><Tooltip title="Reject"><IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); openActionDialog(user, 'REJECT'); }} sx={iconSx}><CancelIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip></>}{['SUSPENDED', 'CANCELLED'].includes(user.accountStatus) ? <Tooltip title="Restore user"><IconButton size="small" color="success" onClick={(e) => { e.stopPropagation(); openActionDialog(user, 'RESTORE'); }} sx={iconSx}><RestoreFromTrashIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip> : user.accountStatus === 'DISABLED' ? <Tooltip title="Enable user"><IconButton size="small" color="success" onClick={(e) => { e.stopPropagation(); openActionDialog(user, 'ENABLE'); }} sx={iconSx}><CheckCircleIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip> : <><Tooltip title="Disable user"><IconButton size="small" color="warning" onClick={(e) => { e.stopPropagation(); openActionDialog(user, 'DISABLE'); }} sx={iconSx}><BlockIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip><Tooltip title="Archive user (reversible)"><IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); openActionDialog(user, 'ARCHIVE'); }} sx={iconSx}><ArchiveIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip></>}</Stack></TableCell></TableRow>)}</TableBody></Table>{!loading && visibleUsers.length > 0 && <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', borderTop: '0.5px solid', borderColor: 'divider' }}><Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} color="primary" shape="rounded" sx={{ '& .MuiPaginationItem-root': { borderRadius: 0 } }} /></Box>}</TableContainer>

      <Dialog open={createOpen} onClose={closeCreateDialog} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle sx={{ fontWeight: 900 }}>Create Customer Account</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, borderRadius: 1 }}>
            The account and starter channel are created in Pending Review. Approving it starts the normal trial, entitlement, and billing workflow.
          </Alert>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
            <TextField autoFocus required label="First name" value={createForm.firstName} onChange={(e) => updateCreateField('firstName', e.target.value)} />
            <TextField required label="Last name" value={createForm.lastName} onChange={(e) => updateCreateField('lastName', e.target.value)} />
            <TextField required type="email" label="Email address" value={createForm.email} onChange={(e) => updateCreateField('email', e.target.value)} />
            <TextField label="Phone" value={createForm.phone} onChange={(e) => updateCreateField('phone', e.target.value)} />
            <TextField required label="Country" value={createForm.country} onChange={(e) => updateCreateField('country', e.target.value)} />
            <TextField select required label="Customer role" value={createForm.role} onChange={(e) => updateCreateField('role', e.target.value as CreateUserForm['role'])}>
              <MenuItem value="BROADCASTER">Broadcaster</MenuItem>
              <MenuItem value="STREAMR">Streamer</MenuItem>
            </TextField>
            <TextField
              required
              label="Starter channel name"
              value={createForm.streamName}
              onChange={(e) => updateCreateField('streamName', e.target.value)}
              helperText="3-40 letters, numbers, underscores, or hyphens."
              sx={{ gridColumn: { md: '1 / -1' } }}
            />
            <TextField
              required
              type="password"
              autoComplete="new-password"
              label="Temporary password"
              value={createForm.password}
              onChange={(e) => updateCreateField('password', e.target.value)}
              helperText="12-256 characters."
            />
            <TextField
              required
              type="password"
              autoComplete="new-password"
              label="Confirm temporary password"
              value={createForm.confirmPassword}
              error={Boolean(createForm.confirmPassword) && createForm.password !== createForm.confirmPassword}
              onChange={(e) => updateCreateField('confirmPassword', e.target.value)}
              helperText={createForm.confirmPassword && createForm.password !== createForm.confirmPassword ? 'Passwords do not match.' : ' '}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateDialog} disabled={createLoading}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={handleCreateUser}
            disabled={createLoading
              || !createForm.firstName.trim()
              || !createForm.lastName.trim()
              || !createForm.email.trim()
              || !createForm.country.trim()
              || !createForm.streamName.trim()
              || createForm.password.length < 12
              || createForm.password !== createForm.confirmPassword}
          >
            {createLoading ? 'Creating...' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!actionType} onClose={closeActionDialog} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle sx={{ fontWeight: 900 }}>{actionTitle}</DialogTitle>
        <DialogContent>
          <Alert severity={actionType === 'DISABLE' || actionType === 'ARCHIVE' || actionType === 'REJECT' ? 'warning' : 'success'} sx={{ mb: 2, borderRadius: 1 }}>
            {actionDescription}
          </Alert>
          {reasonRequired && (
            <TextField
              autoFocus
              required
              label="Reason"
              fullWidth
              multiline
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              helperText="Required and retained in the audit history."
            />
          )}
          {requiresTyped && (
            <TextField
              sx={{ mt: 1.5 }}
              required
              label={`Type ${requiredWord} to confirm`}
              fullWidth
              value={typedConfirm}
              onChange={(e) => setTypedConfirm(e.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeActionDialog}>Cancel</Button>
          <Button
            variant="contained"
            color={actionType === 'DISABLE' || actionType === 'ARCHIVE' || actionType === 'REJECT' ? 'error' : 'success'}
            onClick={handleActionSubmit}
            disabled={actionDisabled}
          >
            {actionLoading ? 'Working...' : actionButtonLabel}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={impersonateOpen} onClose={() => setImpersonateOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 0 } }}><DialogTitle>Controlled Impersonation</DialogTitle><DialogContent><Alert severity="error" sx={{ mb: 2, borderRadius: 0 }}>You are about to enter a user account. The admin session is preserved so you can end impersonation and return to admin.</Alert><Paper elevation={0} sx={{ ...cardSx, p: 1.25, mb: 2 }}><Typography sx={labelSx}>Target user</Typography><Typography sx={{ fontWeight: 900 }}>{impersonateTarget?.email}</Typography><Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{impersonateTarget?.role}</Typography></Paper><Stack spacing={1.5}><TextField label="Reason" fullWidth multiline minRows={3} value={impersonateReason} onChange={(e) => setImpersonateReason(e.target.value)} InputProps={{ sx: { borderRadius: 0 } }} helperText="Required. Example: Investigating destination setup issue." /><TextField label="Type IMPERSONATE to confirm" fullWidth value={impersonateConfirm} onChange={(e) => setImpersonateConfirm(e.target.value)} InputProps={{ sx: { borderRadius: 0 } }} /></Stack></DialogContent><DialogActions><Button onClick={() => setImpersonateOpen(false)}>Cancel</Button><Button variant="contained" color="error" onClick={handleImpersonate} disabled={impersonateLoading || impersonateConfirm !== 'IMPERSONATE' || !impersonateReason.trim()}>{impersonateLoading ? 'Entering...' : 'Start Impersonation'}</Button></DialogActions></Dialog>
    </Box>
  );
};
